import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sun, Moon, Translate, Trash, Warning, Palette } from "@phosphor-icons/react";
import { toast } from "sonner";

function Card({ title, icon: Icon, children }) {
  return (
    <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6" data-testid={`prefs-card-${title}`}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={16} className="text-[#A1A1AA]" />}
        <div className="font-display text-lg font-medium tracking-tight">{title}</div>
      </div>
      {children}
    </div>
  );
}

function SegBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 px-4 py-2.5 rounded-md text-sm transition-colors border ${
        active ? "bg-[#18181B] border-[#5E6AD2] text-white" : "bg-[#0A0A0C] border-[#27272A] text-[#A1A1AA] hover:text-white"
      }`}
    >{children}</button>
  );
}

export default function Preferences() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const nav = useNavigate();
  const [delOpen, setDelOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isOwner = user?.role === "owner";
  const doDelete = async () => {
    if (confirmText !== "ELIMINAR" && confirmText !== "DELETE") {
      return toast.error("Escribe ELIMINAR para confirmar");
    }
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      toast.success("Cuenta eliminada");
      logout();
      nav("/login", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    } finally { setDeleting(false); }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto fade-in" data-testid="preferences-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Account</div>
        <h1 className="font-display text-4xl font-medium tracking-tighter">{t("prefs_title")}</h1>
        <p className="text-sm text-[#A1A1AA] mt-1">{t("prefs_subtitle")}</p>
      </div>

      <div className="space-y-4">
        <Card title={t("prefs_appearance")} icon={Palette}>
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A]">{t("prefs_theme")}</Label>
              <div className="flex gap-2 mt-2">
                <SegBtn active={theme === "dark"} onClick={() => setTheme("dark")} testId="theme-dark">
                  <div className="flex items-center justify-center gap-2"><Moon size={14} weight="fill" /> {t("prefs_theme_dark")}</div>
                </SegBtn>
                <SegBtn active={theme === "light"} onClick={() => setTheme("light")} testId="theme-light">
                  <div className="flex items-center justify-center gap-2"><Sun size={14} weight="fill" /> {t("prefs_theme_light")}</div>
                </SegBtn>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A]">{t("prefs_language")}</Label>
              <div className="flex gap-2 mt-2">
                <SegBtn active={lang === "es"} onClick={() => setLang("es")} testId="lang-es">
                  <div className="flex items-center justify-center gap-2"><Translate size={14} /> {t("prefs_lang_es")}</div>
                </SegBtn>
                <SegBtn active={lang === "en"} onClick={() => setLang("en")} testId="lang-en">
                  <div className="flex items-center justify-center gap-2"><Translate size={14} /> {t("prefs_lang_en")}</div>
                </SegBtn>
              </div>
            </div>
          </div>
        </Card>

        <div className="border border-[rgba(239,68,68,0.35)] bg-[rgba(239,68,68,0.04)] rounded-lg p-6" data-testid="prefs-danger-zone">
          <div className="flex items-center gap-2 mb-2">
            <Warning size={16} className="text-[#EF4444]" weight="fill" />
            <div className="font-display text-lg font-medium tracking-tight text-[#EF4444]">{t("prefs_danger")}</div>
          </div>
          <div className="text-sm mb-1">{t("prefs_delete_title")}</div>
          <p className="text-xs text-[#A1A1AA] mb-4">
            {isOwner ? t("prefs_delete_owner_hint") : t("prefs_delete_member_hint")}
          </p>
          <Button variant="outline" onClick={() => setDelOpen(true)} className="border-[rgba(239,68,68,0.4)] text-[#EF4444] hover:bg-[rgba(239,68,68,0.1)] hover:text-[#EF4444]" data-testid="open-delete-account">
            <Trash size={14} className="mr-1.5" /> {t("prefs_delete_cta")}
          </Button>
        </div>
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="bg-[#121214] border-[#27272A] text-white" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-[#EF4444] flex items-center gap-2">
              <Warning size={18} weight="fill" /> {t("prefs_delete_title")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#A1A1AA]">
            {isOwner ? t("prefs_delete_owner_hint") : t("prefs_delete_member_hint")}
          </p>
          <div>
            <Label>{t("prefs_delete_confirm")}</Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="ELIMINAR" className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb" data-testid="delete-confirm-input" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setDelOpen(false)} className="flex-1 border-[#27272A] bg-[#18181B]" data-testid="delete-cancel">
              Cancelar
            </Button>
            <Button onClick={doDelete} disabled={deleting || (confirmText !== "ELIMINAR" && confirmText !== "DELETE")} className="flex-1 bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-40" data-testid="delete-confirm-button">
              <Trash size={14} className="mr-1.5" /> {deleting ? "Eliminando..." : t("prefs_delete_button")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
